import { useMutation } from "@tanstack/react-query";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import userService, { type SignInReq } from "@/api/services/userService";

import { toast } from "sonner";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	loginTimestamp?: number; // 添加登录时间戳

	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		clearUserInfoAndToken: () => void;
		checkLoginExpiry: () => boolean; // 添加检查登录过期的方法
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: {},
			userToken: {},
			loginTimestamp: undefined,
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					// 设置token时记录当前时间戳
					set({ userToken, loginTimestamp: Date.now() });
				},
				clearUserInfoAndToken() {
					set({ userInfo: {}, userToken: {}, loginTimestamp: undefined });
				},
				checkLoginExpiry: () => {
					const state = get();
					const { loginTimestamp } = state;
					
					// 如果没有登录时间戳，视为未登录
					if (!loginTimestamp) {
						return false;
					}
					
					// 检查是否超过7天 (7 * 24 * 60 * 60 * 1000 = 604800000ms)
					const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
					const isExpired = Date.now() - loginTimestamp > oneWeekInMs;
					
					// 如果过期，清除登录信息
					if (isExpired) {
						state.actions.clearUserInfoAndToken();
						return false;
					}
					
					return true;
				},
			},
		}),
		{
			name: "userStore", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
				loginTimestamp: state.loginTimestamp, // 包含登录时间戳在持久化数据中
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useUserPermissions = () => useUserStore((state) => state.userInfo.permissions || []);
export const useUserRoles = () => useUserStore((state) => state.userInfo.roles || []);
export const useUserActions = () => useUserStore((state) => state.actions);
export const useCheckLoginExpiry = () => useUserStore((state) => state.actions.checkLoginExpiry);

export const useSignIn = () => {
	const { setUserToken, setUserInfo } = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			// 处理新的 API 响应格式
			const { user, accessToken, refreshToken } = res.data;
			setUserToken({ accessToken, refreshToken });
			setUserInfo(user);
		} catch (err) {
			toast.error(err.message, {
				position: "top-center",
			});
			throw err;
		}
	};

	return signIn;
};

export default useUserStore;
